/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for retrieving an individual patient.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create logic based side effects for the redux store.
 */
import { createLogic } from 'redux-logic';

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

 /*
  * Used to register the logics so that they are included in the store. 
  */
import reducerRegistry from '../ReducerRegistry';

/*
 * Used to get access to the API types and requests
 */
import * as Dtos from '../../api/dtos';

/**
 * Enumeration used to determine the state of a request.
 */
import { IRequestState, RequestState } from '.';

/**
 * Used to create a get request to the servicestack API.
 */
import { createGetRequest } from 'src/utils/createRequest';

/**
 * Typings
 */
import { ILogicSideEffect } from 'src/utils/logicTypes';

/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/

interface IInitialVersionState {
   data: Dtos.Version | null;
   loadState: IRequestState;
}

export interface IVersionState {
   version: IInitialVersionState;
}

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const VersionInitialState = {
    data: null,
    loadState: {
        state: RequestState.None
    }
};

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const useVersionSelector: TypedUseSelectorHook<IVersionState> = useSelector;

export const VersionSelectors = {
    selectVersion: (state: IVersionState) => state.version.data,
    selectVersionLoadState: (state: IVersionState) => state.version.loadState
};

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class VersionReducer extends ImmerReducer<IInitialVersionState> {
    public loadById(id: number) {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public loadByCode(code: string) {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public loadSuccess(version: Dtos.Version) {
        this.draftState.data = version;
        this.draftState.loadState = {
            state: RequestState.Success
        };
    }

    public loadFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.data = null;
        this.draftState.loadState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public clear() {
        this.draftState.data = null;
        this.draftState.loadState = {
            state: RequestState.None
        };
    }
}

export const VersionActions = createActionCreators(VersionReducer);
const reducer = createReducerFunction(VersionReducer, VersionInitialState);

/*
 * ---------------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------------
 */

export const VersionApi = {
    loadById: createGetRequest(
        Dtos.GetVersionById,
        (id: number) => ({
            id
        })
    ),
    loadByCode: createGetRequest(
        Dtos.GetVersionByCode,
        (code: string) => ({
            code
        })
    )
}

/*
 * ---------------------------------------------------------------------------------
 * Logic
 * ---------------------------------------------------------------------------------
 */

const VersionLogic = {
    loadById: createLogic({
        type: VersionActions.loadById.type,
        latest: true,
        process: async ({ action }: ILogicSideEffect<IVersionState, typeof VersionActions.loadById>, dispatch, done) => {
            try {
                const response = await VersionApi.loadById(action.payload);
                dispatch(VersionActions.loadSuccess(response.version));
            }
            catch (error) {
                dispatch(VersionActions.loadFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    }),
    loadByCode: createLogic({
        type: VersionActions.loadByCode.type,
        latest: true,
        process: async ({ action }: ILogicSideEffect<IVersionState, typeof VersionActions.loadByCode>, dispatch, done) => {
            try {
                const response = await VersionApi.loadByCode(action.payload);
                dispatch(VersionActions.loadSuccess(response.version));
            }
            catch (error) {
                dispatch(VersionActions.loadFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    })
};

const logics = [
    VersionLogic.loadById,
    VersionLogic.loadByCode
]

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('version', reducer, logics);


// WEBPACK FOOTER //
// ./src/store/reducers/version.ts